import useSWR, { mutate as globalMutate } from 'swr';
import {
  getToolboxTalks,
  createToolboxTalk,
  updateToolboxTalk,
  deleteToolboxTalk
} from '../services/whs/toolboxTalksService';

export function getToolboxTalksCacheKey(filters = {}) {
  return ['toolbox-talks', JSON.stringify(filters)];
}

export function invalidateToolboxTalksCache() {
  return globalMutate(
    key => Array.isArray(key) && key[0] === 'toolbox-talks',
    undefined,
    { revalidate: true }
  );
}

export function useToolboxTalks(filters = {}) {
  const key = getToolboxTalksCacheKey(filters);

  const { data, error, isLoading, mutate } = useSWR(
    key,
    async () => {
      // Fetch Toolbox Talks directly (status mapping is returned in the record payload)
      const result = await getToolboxTalks({
        JobId: filters.job || undefined,
        SearchText: filters.search || undefined,
        PageNumber: filters.page || 1,
        PageSize: filters.limit || 50,
        SelectedColumns: filters.selectedColumns ? filters.selectedColumns.join(',') : undefined,
        SortColumn: filters.sortBy || filters.sortColumn || undefined,
        SortDirection: (filters.sortOrder || filters.sortDirection) ? (filters.sortOrder || filters.sortDirection).toUpperCase() : undefined,
      });

      if (result?.statusCode === 200) {
        let parsedData = result.data;
        if (typeof parsedData === 'string') {
          try {
            parsedData = JSON.parse(parsedData);
          } catch (e) {
            console.error('Failed to parse toolbox talks data JSON string:', e);
            parsedData = [];
          }
        }

        const mappedTalks = (parsedData || []).map(talk => ({
          TalkId: talk.Id,
          Id: talk.Id,
          JobId: talk.JobId,
          Job: talk.JobTitle,
          JobTitle: talk.JobTitle,
          Topic: talk.Topic,
          TalkDate: talk.TalkDate,
          Time: talk.TalkTime ? formatTime12h(talk.TalkTime) : '',
          Status: talk.Status || 'Draft',
          StatusId: talk.StatusId,
          DiscussionPoints: talk.DiscussionPoints || '',
          PresenterNotes: talk.Notes || '',
          Presenter: talk.SupervisorName,
          SupervisorId: talk.SupervisorId,
          AttendeesCount: talk.AttendeeCount || 0,
          DocumentCount: talk.DocumentCount || 0,
          DynamicFields: talk.DynamicFields || "",
          ToolboxTalkNumber: talk.ToolboxTalkNumber
        }));

        return {
          talks: mappedTalks,
          totalRecords: result.totalRecords || 0
        };
      }
      throw new Error(result?.message || 'Failed to fetch toolbox talks');
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 2000,
      keepPreviousData: true,
    }
  );

  const handleCreate = async (payload) => {
    const res = await createToolboxTalk(payload);
    if (res?.statusCode === 200) {
      await invalidateToolboxTalksCache();
      return res;
    }
    throw new Error(res?.message || 'Failed to create toolbox talk');
  };

  const handleUpdate = async (id, payload) => {
    const res = await updateToolboxTalk(id, payload);
    if (res?.statusCode === 200) {
      await invalidateToolboxTalksCache();
      return res;
    }
    throw new Error(res?.message || 'Failed to update toolbox talk');
  };

  const handleDelete = async (id) => {
    const res = await deleteToolboxTalk(id);
    if (res?.statusCode === 200) {
      await invalidateToolboxTalksCache();
      return res;
    }
    throw new Error(res?.message || 'Failed to delete toolbox talk');
  };

  const refresh = () => mutate();

  return {
    talks: data?.talks || [],
    totalRecords: data?.totalRecords || 0,
    isLoading,
    error,
    createTalk: handleCreate,
    updateTalk: handleUpdate,
    deleteTalk: handleDelete,
    refresh
  };
}

// Helper: Converts time format 'HH:mm:ss' or 'HH:mm' to 'hh:mm AM/PM'
function formatTime12h(timeStr) {
  if (!timeStr) return '';
  const parts = timeStr.split(':');
  let hours = parseInt(parts[0], 10);
  const minutes = parts[1] || '00';
  if (isNaN(hours)) return timeStr;

  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // 0 matches 12
  return `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
}
